import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../../utils/cn';

/** Shortest the thumb gets, so a long form still leaves something to grab. */
const MIN_THUMB_PX = 36;
/** How long the indicator lingers after the reader stops scrolling. */
const LINGER_MS = 900;
/** Breathing room between the end of the track and a pinned action bar. */
const PINNED_GAP_PX = 8;

/**
 * Marks a bar pinned inside the scroller (a sticky footer). The indicator
 * stops its track above it and measures scroll against only the part of the
 * pane that actually moves, since the bar itself never scrolls.
 */
export const SCROLL_PINNED_ATTRIBUTE = 'data-scroll-pinned';

export interface ScrollIndicatorProps {
    /** The scroll container it tracks. */
    scrollEl: HTMLElement | null;
    className?: string;
}

interface Metrics {
    scrollable: boolean;
    thumbHeight: number;
    thumbTop: number;
}

/** Where the thumb sits for the container's current scroll, in track pixels. */
export function thumbMetrics(
    scrollTop: number,
    scrollHeight: number,
    clientHeight: number,
    trackHeight: number,
): Metrics {
    const range = scrollHeight - clientHeight;
    if (range <= 1 || trackHeight <= 0) {
        return { scrollable: false, thumbHeight: 0, thumbTop: 0 };
    }
    const thumbHeight = Math.min(
        trackHeight,
        Math.max(MIN_THUMB_PX, (clientHeight / scrollHeight) * trackHeight),
    );
    const progress = Math.min(1, Math.max(0, scrollTop / range));
    return {
        scrollable: true,
        thumbHeight,
        thumbTop: progress * (trackHeight - thumbHeight),
    };
}

/**
 * Ends the track just above a pinned bar, when the step has one, and returns
 * the height that bar takes out of the scrolling viewport.
 */
function fitAbovePinned(scrollEl: HTMLElement, track: HTMLElement): number {
    const pinned = scrollEl.querySelector<HTMLElement>(
        `[${SCROLL_PINNED_ATTRIBUTE}]`,
    );
    const container = track.offsetParent;
    if (!pinned || !container) {
        track.style.bottom = '';
        return 0;
    }
    const box = container.getBoundingClientRect();
    const layoutHeight = (container as HTMLElement).offsetHeight;
    // Screen rects are scaled by the dialog's opening zoom; layout is not.
    const scale =
        layoutHeight > 0 && box.height > 0 ? box.height / layoutHeight : 1;
    const pinnedTop = pinned.getBoundingClientRect().top;
    const scrollerBottom = scrollEl.getBoundingClientRect().bottom;
    track.style.bottom = `${(box.bottom - pinnedTop) / scale + PINNED_GAP_PX}px`;
    return Math.max(0, (scrollerBottom - pinnedTop) / scale);
}

/**
 * A slim scroll position marker for surfaces that hide the native scrollbar.
 *
 * It surfaces while the reader scrolls or rests the pointer on the pane, then
 * fades back out, so the panel reads clean at rest. The thumb follows the
 * scroll without re-rendering: position and size are written straight to its
 * style on the frame they change. A mouse can drag it; touch scrolls the pane
 * itself, as it always would.
 */
export const ScrollIndicator = ({
    scrollEl,
    className,
}: ScrollIndicatorProps) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const thumbRef = useRef<HTMLDivElement>(null);
    const [scrollable, setScrollable] = useState(false);
    const [active, setActive] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [dragging, setDragging] = useState(false);
    const lingerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    const paint = useCallback(() => {
        const track = trackRef.current;
        const thumb = thumbRef.current;
        if (!scrollEl || !track || !thumb) return;
        const pinnedHeight = fitAbovePinned(scrollEl, track);
        const metrics = thumbMetrics(
            scrollEl.scrollTop,
            scrollEl.scrollHeight - pinnedHeight,
            scrollEl.clientHeight - pinnedHeight,
            track.clientHeight,
        );
        setScrollable(metrics.scrollable);
        thumb.style.height = `${metrics.thumbHeight}px`;
        thumb.style.transform = `translateY(${metrics.thumbTop}px)`;
    }, [scrollEl]);

    // Follow the scroll, a frame at a time, and wake the indicator while it moves.
    useEffect(() => {
        if (!scrollEl) return;
        let frame = 0;
        const onScroll = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(paint);
            setActive(true);
            clearTimeout(lingerRef.current);
            lingerRef.current = setTimeout(() => setActive(false), LINGER_MS);
        };
        const onEnter = () => setHovered(true);
        const onLeave = () => setHovered(false);
        // Hover is read on the pane that holds both the scroller and this track,
        // so moving onto the thumb to grab it does not count as leaving.
        const hoverEl = scrollEl.parentElement ?? scrollEl;
        scrollEl.addEventListener('scroll', onScroll, { passive: true });
        hoverEl.addEventListener('pointerenter', onEnter);
        hoverEl.addEventListener('pointerleave', onLeave);
        return () => {
            cancelAnimationFrame(frame);
            clearTimeout(lingerRef.current);
            scrollEl.removeEventListener('scroll', onScroll);
            hoverEl.removeEventListener('pointerenter', onEnter);
            hoverEl.removeEventListener('pointerleave', onLeave);
        };
    }, [scrollEl, paint]);

    // Anything that changes the content's height moves the thumb too: a step
    // swapping, a question appearing, the dialog settling out of its open.
    useEffect(() => {
        if (!scrollEl || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => paint());
        const observeAll = () => {
            observer.disconnect();
            observer.observe(scrollEl);
            for (const child of Array.from(scrollEl.children)) {
                observer.observe(child);
            }
        };
        observeAll();
        // A new step is a new child: watch that one's height as well.
        const children = new MutationObserver(() => {
            observeAll();
            paint();
        });
        children.observe(scrollEl, { childList: true });
        paint();
        return () => {
            observer.disconnect();
            children.disconnect();
        };
    }, [scrollEl, paint]);

    const onThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!scrollEl || event.pointerType === 'touch') return;
        event.preventDefault();
        const track = trackRef.current;
        const thumb = thumbRef.current;
        if (!track || !thumb) return;
        const startY = event.clientY;
        const startScroll = scrollEl.scrollTop;
        const travel = track.clientHeight - thumb.offsetHeight;
        const range = scrollEl.scrollHeight - scrollEl.clientHeight;
        if (travel <= 0) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
        const onMove = (move: PointerEvent) => {
            scrollEl.scrollTop =
                startScroll + ((move.clientY - startY) * range) / travel;
        };
        const onUp = () => {
            setDragging(false);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    const visible = scrollable && (active || hovered || dragging);

    return (
        <div
            ref={trackRef}
            aria-hidden
            className={cn(
                'pointer-events-none absolute top-4 right-1.5 bottom-4 z-[2] w-2.5',
                className,
            )}
        >
            <div
                ref={thumbRef}
                data-state={visible ? 'visible' : 'hidden'}
                data-dragging={dragging || undefined}
                onPointerDown={onThumbPointerDown}
                className={cn(
                    // A hairline at rest that thickens under the pointer, in the
                    // brand's ink at low strength: present, never loud.
                    'absolute right-0 w-1 rounded-full bg-[#171616]/20 transition-[opacity,width,background-color] duration-300 ease-out will-change-transform',
                    'hover:w-1.5 hover:bg-[#171616]/40 data-[dragging]:w-1.5 data-[dragging]:bg-[#171616]/50',
                    visible ? 'pointer-events-auto opacity-100' : 'opacity-0',
                )}
            />
        </div>
    );
};
